import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import type { NomeDoIcone } from '../../design/Icone';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import {
  MODELOS_PARTES,
  MODELOS_TEMPO,
  fmt,
  partes,
  serieNoTempo,
} from '../../design/graficos/modelos';
import { hojeBrasilia } from '../../paineis';
import { lerSemana } from '../../premiacao';
import type { ChequeDaPessoa, ChequeDaSemana } from '../../premiacao';
import { lerAlertas } from '../../validades';
import { foraDaConta, lerRanking, lerVendasDaEquipe } from '../../vendas';
import type { PeriodoDoRanking, ProgramaDeVenda, Ranking, VendasDaEquipe } from '../../vendas';
import type { LancadosNoPeriodo } from '../../lancados-no-periodo';
import type { Periodo } from '../../paineis';
import {
  BarraDeFiltros,
  FILTROS_DOS_PROGRAMAS,
  Kpi,
  irPara,
  lerFiltros,
  nomeCurto,
  pctVar,
  textoDoPeriodo,
} from '../paineis/pecasDaVisao';
import type { Filtro, OpcoesDoFiltro } from '../paineis/pecasDaVisao';
import '../central/central.css';
import './inovapay.css';
import { linhaClicavel } from '../../design/linha-clicavel';
import { semanaDoDia } from '../../design/semana';

/**
 * O MÓDULO DE CADA PROGRAMA (vitamina, pré-vencido, paradão) — tudo do programa num lugar só, em
 * abas: Visão geral · Produtos · Lançamentos · Conferência · Ranking. Os números da conferência
 * são os do cheque de sábado (a mesma conta do pagamento), só daquele programa.
 */

export type ChaveDoPrograma = 'vitaminas' | 'pre-vencidos' | 'paradao';
type Config = {
  readonly nome: string;
  readonly programa: ProgramaDeVenda;
  readonly cor: string;
  readonly icone: NomeDoIcone;
  /** Rótulo da aba de produtos (cada programa tem o seu cadastro). */
  readonly produtos: string;
  /** Campo do ranking com a contagem deste programa. */
  readonly noRanking: 'vitaminas' | 'preVencidos' | 'paradao';
};
export const PROGRAMAS: Readonly<Record<ChaveDoPrograma, Config>> = {
  vitaminas: {
    nome: 'Vitaminas',
    programa: 'vitamina',
    cor: '#1a7f55',
    icone: 'pill',
    produtos: 'Produtos e faixas',
    noRanking: 'vitaminas',
  },
  'pre-vencidos': {
    nome: 'Pré-vencidos',
    programa: 'pre_vencido',
    cor: '#cb1b2b',
    icone: 'clock',
    produtos: 'Lotes',
    noRanking: 'preVencidos',
  },
  paradao: {
    nome: 'Paradão',
    programa: 'paradao',
    cor: '#b7791f',
    icone: 'box',
    produtos: 'Produtos parados',
    noRanking: 'paradao',
  },
};

export type AbaDoPrograma =
  | 'visao'
  | 'produtos'
  | 'vencimentos'
  | 'vencidos'
  | 'alertas'
  | 'lancamentos'
  | 'conferencia'
  | 'ranking';
/** Abas que só o Pré-vencidos tem (as da tela de lotes, trazidas para a barra do módulo). */
const ABAS_DO_PRE_VENCIDO: readonly AbaDoPrograma[] = ['vencimentos', 'vencidos', 'alertas'];
/** A rota de cada aba: #/vitaminas, #/vitaminas-produtos, #/vitaminas-lancamentos… */
export const rotaDaAba = (chave: ChaveDoPrograma, aba: AbaDoPrograma) =>
  aba === 'visao' ? chave : `${chave}-${aba}`;
export function abaDaRota(rota: string): { chave: ChaveDoPrograma; aba: AbaDoPrograma } | null {
  for (const chave of Object.keys(PROGRAMAS) as ChaveDoPrograma[]) {
    if (rota === chave) return { chave, aba: 'visao' };
    for (const aba of ['produtos', 'lancamentos', 'conferencia', 'ranking'] as const)
      if (rota === `${chave}-${aba}`) return { chave, aba };
    if (chave === 'pre-vencidos')
      for (const aba of ABAS_DO_PRE_VENCIDO) if (rota === `${chave}-${aba}`) return { chave, aba };
  }
  return null;
}

/* ------------------------------------------------------------------ *
 * O FILTRO DE PERÍODO dos programas (dono, 05/10): em TODAS as telas de vitamina, pré-vencido e
 * paradão — esta semana, semana passada, este mês, mês passado e período. Mora no endereço
 * (p, de, ate) e vai junto quando se troca de aba.
 * ------------------------------------------------------------------ */

const CHAVES_DO_PERIODO = ['p', 'de', 'ate'] as const;

/** O filtro das telas do programa: começa na semana do cheque (sábado a sexta). */
export function filtroDoPrograma(parametros: URLSearchParams, opcoes: OpcoesDoFiltro = {}) {
  return lerFiltros(parametros, hojeBrasilia(), { padrao: 'semana', ...opcoes });
}

/** Barra do período com os cinco botões dos programas. */
export function BarraDoPrograma({
  f,
  rota,
  extras,
  padrao = 'semana',
  futuro,
  maxDias,
  semFiltro,
  children,
}: {
  f: ReturnType<typeof lerFiltros>;
  rota: string;
  extras?: Readonly<Record<string, string | null | undefined>>;
  padrao?: Filtro;
  futuro?: boolean;
  maxDias?: number;
  semFiltro?: string;
  children?: ReactNode;
}) {
  return (
    <BarraDeFiltros
      f={f}
      hoje={hojeBrasilia()}
      rota={rota}
      filtros={FILTROS_DOS_PROGRAMAS}
      padrao={padrao}
      {...(extras ? { extras } : {})}
      {...(futuro ? { futuro } : {})}
      {...(maxDias ? { maxDias } : {})}
      {...(semFiltro ? { semFiltro } : {})}
    >
      {children}
    </BarraDeFiltros>
  );
}

/** O período escolhido na aba atual, para levar junto na troca de aba. */
function periodoDoEndereco(): string {
  const q = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  const leva = new URLSearchParams();
  for (const k of CHAVES_DO_PERIODO) {
    const v = q.get(k);
    if (v) leva.set(k, v);
  }
  const s = leva.toString();
  return s ? `?${s}` : '';
}

const diasDe = (p: Periodo) => (Date.parse(p.ate) - Date.parse(p.de)) / 86_400_000;

/** Quantos alertas de validade estão abertos (o contador da aba Alertas do Pré-vencidos). */
function useAlertasAbertos(ligado: boolean): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!ligado) return;
    let vivo = true;
    void lerAlertas().then((r) => {
      if (vivo && r.ok) setN(r.dados.ativos.filter((a) => a.estado === 'aberto').length);
    });
    return () => {
      vivo = false;
    };
  }, [ligado]);
  return n;
}

export function SubnavDoPrograma({
  chave,
  atual,
  direita,
}: {
  chave: ChaveDoPrograma;
  atual: AbaDoPrograma;
  direita?: string;
}) {
  const c = PROGRAMAS[chave];
  const alertas = useAlertasAbertos(chave === 'pre-vencidos');
  const leva = periodoDoEndereco();
  const abas: readonly [AbaDoPrograma, string][] = [
    ['visao', 'Visão geral'],
    ['produtos', c.produtos],
    ...(chave === 'pre-vencidos'
      ? ([
          ['vencimentos', 'Vencimentos'],
          ['vencidos', 'Vencidos por mês'],
          ['alertas', 'Alertas'],
        ] as [AbaDoPrograma, string][])
      : []),
    ['lancamentos', 'Lançamentos'],
    ['conferencia', 'Conferência'],
    ['ranking', 'Ranking'],
  ];
  return (
    <nav className="cv-subnav ip-subnav" aria-label={c.nome}>
      <span className="ip-subnav-nome" style={{ color: c.cor }}>
        <Icone nome={c.icone} tamanho={15} traco={1.7} />
        {c.nome}
      </span>
      {abas.map(([aba, rotulo]) => (
        <span key={aba} className="cv-subnav-item">
          <a
            href={`#/${rotaDaAba(chave, aba)}${leva}`}
            className={atual === aba ? 'cv-sel' : ''}
            aria-current={atual === aba ? 'page' : undefined}
          >
            {rotulo}
            {aba === 'alertas' && alertas > 0 && <span className="cv-contador">{alertas}</span>}
          </a>
        </span>
      ))}
      <span style={{ flex: 1 }} />
      {direita && <span className="cv-subnav-direita">{direita}</span>}
    </nav>
  );
}

/* ------------------------------------------------------------------ *
 * Semana do cheque (sábado a sexta desde 24/10/2026) e a navegação entre semanas
 * ------------------------------------------------------------------ */
export const semanaDoCheque = semanaDoDia;
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const soma = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);

/** O que o cheque diz DESTE programa para uma pessoa. */
function contasDoPrograma(p: ChequeDaPessoa, programa: ProgramaDeVenda) {
  const doP = <T extends { programa: string; qtd: number }>(xs: readonly T[]) =>
    xs.filter((x) => x.programa === programa);
  const bate = doP(p.linhas.bate);
  return {
    bateu: soma(bate.map((l) => l.qtd)),
    naoVendeu: soma(doP(p.linhas.naoVendeu).map((l) => l.qtd)),
    naoLancou: soma(doP(p.linhas.naoLancou).map((l) => l.qtd)),
    diaErrado: soma(doP(p.linhas.diaErrado).map((l) => l.qtd)),
    loteDoDia: soma(doP(p.linhas.loteDoMesmoDia ?? []).map((l) => l.qtd)),
    devolvidos: soma(doP(p.linhas.devolvidos).map((l) => l.qtd)),
    cancelados: soma(doP(p.linhas.canceladas).map((l) => l.qtd)),
    devido: p.tipos[programa].devido,
  };
}

/* ------------------------------------------------------------------ *
 * VISÃO GERAL do programa
 * ------------------------------------------------------------------ */

export function VisaoDoPrograma({
  chave,
  parametros,
}: {
  chave: ChaveDoPrograma;
  parametros: URLSearchParams;
}) {
  const c = PROGRAMAS[chave];
  const hoje = hojeBrasilia();
  const f = filtroDoPrograma(parametros);
  const { de, ate } = f.periodo;
  // A conferência acompanha o período (a conta do cheque aceita até 62 dias); período maior
  // fica com a semana do cheque.
  const semana = diasDe(f.periodo) <= 62 ? f.periodo : semanaDoCheque(hoje);
  const [d, setD] = useState<VendasDaEquipe | null>(null);
  const [ch, setCh] = useState<ChequeDaSemana | null>(null);
  const [erro, setErro] = useState(false);
  const [erroDaSemana, setErroDaSemana] = useState(false);
  const carregarSemana = useCallback(() => {
    setErroDaSemana(false);
    void lerSemana(semana.de, semana.ate).then((r) =>
      r.ok ? setCh(r.dados) : setErroDaSemana(true),
    );
  }, [semana.de, semana.ate]);
  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerVendasDaEquipe({ de, ate, programa: c.programa }).then((r) =>
      r.ok ? setD(r.dados) : setErro(true),
    );
    carregarSemana();
  }, [de, ate, c.programa, carregarSemana]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const ativas = d ? d.vendas.filter((v) => !foraDaConta(v.status)) : [];
  const porProduto = [
    ...ativas.reduce(
      (m, v) => m.set(v.produto, (m.get(v.produto) ?? 0) + v.quantidade),
      new Map<string, number>(),
    ),
  ].sort((a, b) => b[1] - a[1]);
  const porPessoa = [
    ...ativas.reduce(
      (m, v) => m.set(v.balconista, (m.get(v.balconista) ?? 0) + v.quantidade),
      new Map<string, number>(),
    ),
  ].sort((a, b) => b[1] - a[1]);
  const blocos = d
    ? agrupar(d.porDia.map((x) => ({ dia: x.dia, faturamento: x.itens, cupons: 0, itens: 0 })))
    : [];
  const melhor = [...blocos].sort((a, b) => b.faturamento - a.faturamento)[0];
  const conf = ch ? ch.pessoas.map((p) => contasDoPrograma(p, c.programa)) : [];
  const confSoma = (k: keyof ReturnType<typeof contasDoPrograma>) => soma(conf.map((x) => x[k]));
  const variacao =
    d && d.anterior?.itens
      ? pctVar(
          Math.round(((d.resumo.itens - d.anterior.itens) / d.anterior.itens) * 1000) / 10,
          'período anterior',
        )
      : null;

  return (
    <section className="cv">
      <SubnavDoPrograma chave={chave} atual="visao" />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>{c.nome}</h1>
            <div className="vg-contexto">
              O programa inteiro: o que foi lançado, conferido e premiado · {quando}
            </div>
          </div>
          <div className="vg-acoes">
            <a
              className="vg-botao"
              href={`#/${rotaDaAba(chave, 'produtos')}${periodoDoEndereco()}`}
            >
              <Icone nome={c.icone} tamanho={15} traco={1.6} />
              {c.produtos}
            </a>
          </div>
        </header>
        <BarraDoPrograma f={f} rota={chave} />

        {erro ? (
          <ErroDeArea oQue={`o programa ${c.nome}`} aoTentar={carregar} />
        ) : (
          <>
            <div className="vg-kpis">
              <Kpi
                rotulo={`Itens lançados · ${quando}`}
                icone={c.icone}
                valor={d ? fmt(d.resumo.itens, 'inteiro') : null}
                variacao={variacao}
                trilha={d?.porDia.map((x) => x.itens)}
                link={`#/${rotaDaAba(chave, 'lancamentos')}`}
                destaque
              />
              <Kpi
                rotulo="Prêmio gerado"
                icone="trophy"
                valor={d ? fmt(Number(d.resumo.premiacao), 'reais') : null}
                variacao={null}
                sub={d ? `em ${fmt(Number(d.resumo.valorVendido), 'reais')} vendidos` : null}
                link={`#/${rotaDaAba(chave, 'lancamentos')}`}
              />
              <Kpi
                rotulo="Quem lançou"
                icone="users"
                valor={d ? fmt(porPessoa.length, 'inteiro') : null}
                variacao={null}
                sub={d ? 'balconistas no período' : null}
                link={`#/${rotaDaAba(chave, 'ranking')}`}
              />
              <Kpi
                rotulo="Conferido no período"
                icone="check"
                valor={
                  ch ? `${fmt(confSoma('bateu'), 'inteiro')} itens` : erroDaSemana ? '—' : null
                }
                variacao={null}
                sub={
                  ch
                    ? `${fmt(confSoma('devido'), 'reais')} a pagar · ${diaCurto(semana.de)} a ${diaCurto(semana.ate)}`
                    : null
                }
                link={`#/${rotaDaAba(chave, 'conferencia')}`}
              />
              <Kpi
                rotulo="Lançou e não vendeu"
                icone="warn"
                valor={
                  ch ? `${fmt(confSoma('naoVendeu'), 'inteiro')} itens` : erroDaSemana ? '—' : null
                }
                variacao={null}
                sub={ch ? 'no período, sem a venda no HOS' : null}
                link={`#/${rotaDaAba(chave, 'conferencia')}`}
              />
              <Kpi
                rotulo="Vendeu e não lançou"
                icone="scan"
                valor={
                  ch ? `${fmt(confSoma('naoLancou'), 'inteiro')} itens` : erroDaSemana ? '—' : null
                }
                variacao={null}
                sub={ch ? 'prêmio que ficou na mesa' : null}
                link={`#/${rotaDaAba(chave, 'conferencia')}`}
              />
            </div>
            {erroDaSemana && !ch && (
              <ErroDeArea oQue="a conferência do período" aoTentar={carregarSemana} />
            )}

            {d && ativas.length > 0 ? (
              <>
                <CartaoDeGrafico
                  id={`pg-${chave}-dia`}
                  titulo="Itens lançados no tempo"
                  numero={fmt(d.resumo.itens, 'inteiro')}
                  conclusao={
                    melhor ? (
                      <>
                        Melhor {blocos.length === d.porDia.length ? 'dia' : 'período'}:{' '}
                        <strong>{melhor.rotulo}</strong> ({fmt(melhor.faturamento, 'inteiro')}{' '}
                        itens).
                      </>
                    ) : null
                  }
                  modelos={MODELOS_TEMPO}
                  padrao="area"
                  desenhar={(m) =>
                    serieNoTempo(m, {
                      cats: blocos.map((b) => b.rotulo),
                      atual: blocos.map((b) => b.faturamento),
                      anterior: blocos.map(() => null),
                      nome: 'Itens',
                      formato: 'inteiro',
                    })
                  }
                  fonte="Fonte: lançamentos do InovaPay"
                  altura={240}
                />
                <div className="vg-linha cv-linha-2">
                  <CartaoDeGrafico
                    id={`pg-${chave}-produtos`}
                    titulo="Produtos que mais saem"
                    numero={porProduto[0] ? `${fmt(porProduto[0][1], 'inteiro')} itens` : '—'}
                    conclusao={
                      porProduto[0] ? (
                        <>
                          <strong>{porProduto[0][0]}</strong> lidera;{' '}
                          {fmt(
                            (soma(porProduto.slice(0, 10).map((x) => x[1])) /
                              Math.max(1, soma(porProduto.map((x) => x[1])))) *
                              100,
                            'pct',
                          )}{' '}
                          dos itens estão nos 10 primeiros, de {fmt(porProduto.length, 'inteiro')}{' '}
                          produtos.
                        </>
                      ) : null
                    }
                    modelos={MODELOS_PARTES}
                    padrao="barras"
                    desenhar={(m) =>
                      partes(m, {
                        itens: porProduto.slice(0, 10).map(([nome, valor]) => ({
                          nome: nome.length > 28 ? `${nome.slice(0, 27)}…` : nome,
                          valor,
                        })),
                        formato: 'inteiro',
                      })
                    }
                    fonte="Fonte: lançamentos do InovaPay"
                    altura={300}
                  />
                  <CartaoDeGrafico
                    id={`pg-${chave}-pessoas`}
                    titulo="Quem mais lança"
                    numero={
                      porPessoa[0]
                        ? `${nomeCurto(porPessoa[0][0])} · ${fmt(porPessoa[0][1], 'inteiro')}`
                        : '—'
                    }
                    conclusao={
                      porPessoa.length > 1 ? (
                        <>
                          Do primeiro ao último, de {fmt(porPessoa[0]?.[1] ?? 0, 'inteiro')} a{' '}
                          {fmt(porPessoa.at(-1)?.[1] ?? 0, 'inteiro')} itens. Veja o ranking
                          completo na aba Ranking.
                        </>
                      ) : null
                    }
                    modelos={MODELOS_PARTES}
                    padrao="barras"
                    desenhar={(m) =>
                      partes(m, {
                        itens: porPessoa
                          .slice(0, 10)
                          .map(([nome, valor]) => ({ nome: nomeCurto(nome), valor })),
                        formato: 'inteiro',
                      })
                    }
                    fonte="Fonte: lançamentos do InovaPay"
                    altura={300}
                  />
                </div>
              </>
            ) : d ? (
              <div className="vg-bloco">
                <p className="vg-vazio">
                  Nenhum item de {c.nome.toLowerCase()} lançado no InovaPay 2.0 neste período.
                </p>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ *
 * CONFERÊNCIA do programa (a semana do cheque, só deste programa)
 * ------------------------------------------------------------------ */

export function ConferenciaDoPrograma({
  chave,
  parametros,
}: {
  chave: ChaveDoPrograma;
  parametros: URLSearchParams;
}) {
  const c = PROGRAMAS[chave];
  // Endereço antigo (?semana=AAAA-MM-DD) ainda abre aquela semana do cheque.
  const pedida = parametros.get('semana');
  const antiga =
    !parametros.get('p') && pedida && /^\d{4}-\d{2}-\d{2}$/.test(pedida)
      ? semanaDoCheque(pedida)
      : null;
  const f = antiga
    ? { filtro: 'livre' as const, periodo: antiga, pessoa: null }
    : filtroDoPrograma(parametros);
  const semana = f.periodo;
  const [ch, setCh] = useState<ChequeDaSemana | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setCh(null);
    void lerSemana(semana.de, semana.ate).then((r) => (r.ok ? setCh(r.dados) : setErro(true)));
  }, [semana.de, semana.ate]);
  useEffect(carregar, [carregar]);

  const linhas = ch
    ? ch.pessoas
        .map((p) => ({ p, x: contasDoPrograma(p, c.programa) }))
        .filter(
          ({ x }) =>
            x.bateu +
              x.naoVendeu +
              x.naoLancou +
              x.diaErrado +
              x.loteDoDia +
              x.devolvidos +
              x.cancelados >
            0,
        )
        .sort((a, b) => b.x.devido - a.x.devido || b.x.bateu - a.x.bateu)
    : [];
  const tot = (k: keyof ReturnType<typeof contasDoPrograma>) => soma(linhas.map((l) => l.x[k]));

  return (
    <section className="cv">
      <SubnavDoPrograma
        chave={chave}
        atual="conferencia"
        direita="a mesma conta do cheque de sábado"
      />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Conferência · {c.nome}</h1>
            <div className="vg-contexto">
              De {diaCurto(semana.de)} a {diaCurto(semana.ate)} · o lançado de{' '}
              {c.nome.toLowerCase()} contra as vendas do HOS, por vendedor
            </div>
          </div>
          <div className="vg-acoes">
            <a className="vg-botao vg-botao-escuro" href="#/conferencia">
              <Icone nome="check" tamanho={15} traco={1.6} />
              Conferência completa
            </a>
          </div>
        </header>
        <BarraDoPrograma f={f} rota={rotaDaAba(chave, 'conferencia')} maxDias={62} />

        {erro ? (
          <ErroDeArea oQue="a conferência" aoTentar={carregar} />
        ) : (
          <>
            <div className="ip-contas">
              <Conta rotulo="A pagar" valor={ch ? fmt(tot('devido'), 'reais') : null} tom="ok" />
              <Conta
                rotulo="Bateu com o HOS"
                valor={ch ? `${fmt(tot('bateu'), 'inteiro')} itens` : null}
                tom="ok"
              />
              <Conta
                rotulo="Lançou e não vendeu"
                valor={ch ? `${fmt(tot('naoVendeu'), 'inteiro')} itens` : null}
                tom="ruim"
              />
              <Conta
                rotulo="Vendeu e não lançou"
                valor={ch ? `${fmt(tot('naoLancou'), 'inteiro')} itens` : null}
                tom="atencao"
              />
              <Conta
                rotulo="Dia errado"
                valor={ch ? `${fmt(tot('diaErrado'), 'inteiro')} itens` : null}
                tom="atencao"
              />
              {c.programa === 'pre_vencido' && (
                <Conta
                  rotulo="Lote vendido no dia em que entrou"
                  valor={ch ? `${fmt(tot('loteDoDia'), 'inteiro')} itens` : null}
                  tom="atencao"
                />
              )}
              <Conta
                rotulo="Devolvidos · cancelados"
                valor={
                  ch
                    ? `${fmt(tot('devolvidos'), 'inteiro')} · ${fmt(tot('cancelados'), 'inteiro')}`
                    : null
                }
                tom="neutro"
              />
            </div>

            <div className="vg-bloco cv-lista">
              {!ch ? (
                <div className="vg-lista-osso" aria-busy="true">
                  {[0, 1, 2, 3].map((k) => (
                    <i key={k} />
                  ))}
                </div>
              ) : linhas.length === 0 ? (
                <p className="cv-vazio">
                  Ninguém lançou ou vendeu {c.nome.toLowerCase()} neste período.
                </p>
              ) : (
                <div className="cv-rolagem">
                  <table className="cv-tabela">
                    <thead>
                      <tr>
                        <th>Vendedor</th>
                        <th className="cv-dir">Bateu</th>
                        <th className="cv-dir">Lançou e não vendeu</th>
                        <th className="cv-dir">Vendeu e não lançou</th>
                        <th className="cv-dir">Dia errado</th>
                        {c.programa === 'pre_vencido' && <th className="cv-dir">Lote do dia</th>}
                        <th className="cv-dir">Devolvidos</th>
                        <th className="cv-dir">Cancelados</th>
                        <th className="cv-dir">A pagar ({c.nome.toLowerCase()})</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhas.map(({ p, x }) => (
                        <tr
                          key={p.pessoaId}
                          className="cv-clicavel"
                          {...linhaClicavel(
                            () =>
                              (window.location.hash = `#/conferencia?pessoa=${p.pessoaId}&semana=${semana.de}`),
                          )}
                        >
                          <td>
                            <div className="cv-forte">{p.nome}</div>
                            <div className="cv-cinza cv-peq">
                              {p.codigo ? `cód ${p.codigo}` : ''}
                            </div>
                          </td>
                          <td className="cv-dir cv-forte">{fmt(x.bateu, 'inteiro')}</td>
                          <td className={`cv-dir ${x.naoVendeu ? 'cv-neg cv-forte' : 'cv-cinza'}`}>
                            {fmt(x.naoVendeu, 'inteiro')}
                          </td>
                          <td
                            className={`cv-dir ${x.naoLancou ? 'cv-alerta-txt cv-forte' : 'cv-cinza'}`}
                          >
                            {fmt(x.naoLancou, 'inteiro')}
                          </td>
                          <td className={`cv-dir ${x.diaErrado ? 'cv-alerta-txt' : 'cv-cinza'}`}>
                            {fmt(x.diaErrado, 'inteiro')}
                          </td>
                          {c.programa === 'pre_vencido' && (
                            <td className={`cv-dir ${x.loteDoDia ? 'cv-alerta-txt' : 'cv-cinza'}`}>
                              {fmt(x.loteDoDia, 'inteiro')}
                            </td>
                          )}
                          <td className="cv-dir cv-cinza">{fmt(x.devolvidos, 'inteiro')}</td>
                          <td className="cv-dir cv-cinza">{fmt(x.cancelados, 'inteiro')}</td>
                          <td className="cv-dir cv-forte">{fmt(x.devido, 'reais')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="cv-paginacao">
                <span>{ch ? `${fmt(linhas.length, 'inteiro')} vendedor(es)` : ''}</span>
                <span>clique no vendedor para abrir a conferência dele, linha a linha</span>
              </div>
            </div>
          </>
        )}
      </div>
    </section>
  );
}

export function Conta({
  rotulo,
  valor,
  tom,
}: {
  rotulo: string;
  valor: string | null;
  tom: 'ok' | 'ruim' | 'atencao' | 'neutro';
}) {
  return (
    <div className={`ip-conta ip-conta-${tom}`}>
      <strong>{valor ?? '…'}</strong>
      <span>{rotulo}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * RANKING do programa
 * ------------------------------------------------------------------ */

/** Botão pronto → período do servidor; "Período" vai como datas. */
const PERIODO_DO_RANKING: Partial<Record<Filtro, PeriodoDoRanking>> = {
  semana: 'semana',
  semana_passada: 'semana_passada',
  mes: 'mes',
  mes_passado: 'mes_passado',
};

export function RankingDoPrograma({
  chave,
  parametros,
}: {
  chave: ChaveDoPrograma;
  parametros: URLSearchParams;
}) {
  const c = PROGRAMAS[chave];
  const f = filtroDoPrograma(parametros);
  const pronto = PERIODO_DO_RANKING[f.filtro];
  const { de, ate } = f.periodo;
  const [r, setR] = useState<Ranking | null>(null);
  const [erro, setErro] = useState(false);
  const carregar = useCallback(() => {
    setErro(false);
    setR(null);
    void lerRanking(pronto ?? { de, ate }).then((x) => (x.ok ? setR(x.dados) : setErro(true)));
  }, [pronto, de, ate]);
  useEffect(carregar, [carregar]);
  const linhas = r
    ? [...r.linhas]
        .filter((l) => l[c.noRanking] > 0)
        .sort((a, b) => b[c.noRanking] - a[c.noRanking])
    : [];
  const total = soma(linhas.map((l) => l[c.noRanking]));

  return (
    <section className="cv">
      <SubnavDoPrograma chave={chave} atual="ranking" />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Ranking · {c.nome}</h1>
            <div className="vg-contexto">
              {r ? `${diaCurto(r.de)} a ${diaCurto(r.ate)} · ` : ''}quem mais lançou{' '}
              {c.nome.toLowerCase()}
            </div>
          </div>
        </header>
        <BarraDoPrograma f={f} rota={rotaDaAba(chave, 'ranking')} />
        {erro ? (
          <ErroDeArea oQue="o ranking" aoTentar={carregar} />
        ) : !r ? (
          <div className="gx-cartao vg-osso" style={{ height: 320 }} aria-busy="true" />
        ) : linhas.length === 0 ? (
          <div className="vg-bloco">
            <p className="vg-vazio">Ninguém lançou {c.nome.toLowerCase()} neste período.</p>
          </div>
        ) : (
          <div className="vg-linha cv-linha-2">
            <CartaoDeGrafico
              id={`pg-${chave}-ranking`}
              titulo={`Itens de ${c.nome.toLowerCase()} por balconista`}
              numero={`${fmt(total, 'inteiro')} itens`}
              conclusao={
                linhas[0] ? (
                  <>
                    <strong>{nomeCurto(linhas[0].nome)}</strong> lidera com{' '}
                    {fmt(linhas[0][c.noRanking], 'inteiro')} itens (
                    {fmt((linhas[0][c.noRanking] / Math.max(1, total)) * 100, 'pct')} do total).
                  </>
                ) : null
              }
              modelos={MODELOS_PARTES}
              padrao="barras"
              desenhar={(m) =>
                partes(m, {
                  itens: linhas
                    .slice(0, 15)
                    .map((l) => ({ nome: nomeCurto(l.nome), valor: l[c.noRanking] })),
                  formato: 'inteiro',
                })
              }
              fonte="Fonte: lançamentos do InovaPay"
              altura={Math.max(260, Math.min(15, linhas.length) * 26 + 60)}
            />
            <div className="vg-bloco">
              <div className="vg-bloco-topo">
                <div>
                  <h2>Posições</h2>
                  <p>Meta da semana: {fmt(r.metaSemanal, 'inteiro')} itens somando os programas</p>
                </div>
              </div>
              <ol className="vg-ranking">
                {linhas.map((l, i) => (
                  <li key={l.accountId}>
                    <span className={`vg-pos vg-pos-${String(Math.min(4, i + 1))}`}>{i + 1}</span>
                    <span className="vg-avatar">
                      {nomeCurto(l.nome)
                        .split(' ')
                        .map((x) => x[0])
                        .join('')}
                    </span>
                    <span className="vg-rank-nome">
                      <strong>{nomeCurto(l.nome)}</strong>
                      <span>
                        cód {l.codigo} · {fmt(l.itens, 'inteiro')} itens em todos os programas
                      </span>
                      <span className="vg-rank-barra">
                        <i
                          style={{
                            width: `${String((l[c.noRanking] / Math.max(1, linhas[0]?.[c.noRanking] ?? 1)) * 100)}%`,
                          }}
                        />
                      </span>
                    </span>
                    <span className="vg-rank-valor">
                      <strong>{fmt(l[c.noRanking], 'inteiro')}</strong>
                      <span>itens</span>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/** O que as abas de produtos recebem do filtro de período. */
export type PeriodoDaAba = {
  /** Produtos, lotes e parados: quanto de cada um foi lançado no período. */
  readonly lancados: LancadosNoPeriodo;
  /**
   * Vencimentos e Vencidos: o período pela VALIDADE do lote. Alertas: pela data da decisão
   * (lido/tratado). null = sem período — a tela de sempre.
   */
  readonly validade: Periodo | null;
};

/** Aba "Produtos": a tela de cada programa, com a subnav do módulo e o filtro de período em cima. */
export function ProdutosDoPrograma({
  chave,
  aba = 'produtos',
  parametros,
  children,
}: {
  chave: ChaveDoPrograma;
  aba?: AbaDoPrograma;
  parametros: URLSearchParams;
  children: (periodo: PeriodoDaAba) => ReactNode;
}) {
  const rota = rotaDaAba(chave, aba);
  const deValidade = ABAS_DO_PRE_VENCIDO.includes(aba);
  // As telas de validade começam sem período ("hoje" não é botão: nenhum fica aceso) e olham
  // para frente — "este mês" é o mês inteiro, o período pode passar de hoje.
  const f = deValidade
    ? filtroDoPrograma(parametros, { padrao: 'hoje', futuro: aba !== 'alertas' })
    : filtroDoPrograma(parametros);
  const so = parametros.get('so') === '1';
  return (
    <>
      <SubnavDoPrograma chave={chave} atual={aba} />
      <div className="vg ip-filtro-da-aba">
        {deValidade ? (
          <BarraDoPrograma
            f={f}
            rota={rota}
            padrao="hoje"
            futuro={aba !== 'alertas'}
            semFiltro={
              aba === 'alertas'
                ? 'Sem período: o histórico inteiro'
                : aba === 'vencidos'
                  ? 'Sem período: escolha o mês abaixo'
                  : 'Sem período: as faixas de validade'
            }
          />
        ) : (
          <BarraDoPrograma f={f} rota={rota} extras={{ so: so ? '1' : null }}>
            <label className="vg-pessoa ip-so-lancados">
              <input
                type="checkbox"
                checked={so}
                onChange={(e) =>
                  irPara(
                    { filtro: f.filtro, periodo: f.periodo, pessoa: null },
                    rota,
                    { so: e.target.checked ? '1' : null },
                    'semana',
                  )
                }
              />
              Só os lançados no período
            </label>
          </BarraDoPrograma>
        )}
      </div>
      {children({
        lancados: { de: f.periodo.de, ate: f.periodo.ate, so },
        validade: deValidade && f.filtro !== 'hoje' ? f.periodo : null,
      })}
    </>
  );
}
